export const DealerJackProtocol = {
  // 게임 시작/종료
  GAME_START: "dealerjack:game_start",
  ROUND_START: "dealerjack:round_start",
  ROUND_END: "dealerjack:round_end",
  GAME_END: "dealerjack:game_end",

  // 베팅 관련
  BET_PLACED: "dealerjack:bet_placed",
  BETTING_TIME_END: "dealerjack:betting_time_end",

  // 카드 관련
  CARDS_DEALT: "dealerjack:cards_dealt",
  CARD_HIT: "dealerjack:card_hit",
  PLAYER_STAND: "dealerjack:player_stand",

  // 턴 관리
  PLAYER_TURN: "dealerjack:player_turn",
  PLAYER_ACTION: "dealerjack:player_action",
  PLAYER_ACTION_COMPLETED: "dealerjack:player_action_completed",
  DEALER_TURN: "dealerjack:dealer_turn",

  // 결과
  HAND_RESULT: "dealerjack:hand_result",
  ROUND_RESULTS: "dealerjack:round_results",

  // 파산 관련
  PLAYER_ELIMINATED: "dealerjack:player_eliminated",

  // 클라이언트 요청
  BET_REQUEST: "dealerjack:bet_request",
  HIT_REQUEST: "dealerjack:hit_request",
  STAND_REQUEST: "dealerjack:stand_request",
  DOUBLE_DOWN_REQUEST: "dealerjack:double_down_request",
  SURRENDER_REQUEST: "dealerjack:surrender_request",
  SKIP_RESULTS_REQUEST: "dealerjack:skip_results_request",

  // 재연결
  SYNC_GAME_STATE: "dealerjack:sync_game_state",

  // 에러
  ERROR: "dealerjack:error",
} as const;

export type DealerJackProtocolType =
  typeof DealerJackProtocol[keyof typeof DealerJackProtocol];

// 하위 호환성을 위한 alias
export const BlackjackProtocol = DealerJackProtocol;
export type BlackjackProtocolType = DealerJackProtocolType;

// 카드 인터페이스
export interface Card {
  suit: "hearts" | "diamonds" | "clubs" | "spades";
  rank:
    | "A"
    | "2"
    | "3"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | "10"
    | "J"
    | "Q"
    | "K";
  value: number; // 계산된 값 (A는 상황에 따라 1 또는 11)
}

// 딜러잭 플레이어 인터페이스
export interface DealerJackPlayer {
  id: number;
  name: string;
  avatar: string;
  isBot: boolean;
  isHost: boolean;
  chips: number;
  currentBet: number;
  cards: Card[];
  handValue: number;
  isStanding: boolean;
  isBust: boolean;
  isBlackjack: boolean;
  isActive: boolean;
  position: number; // UI 배치용 위치 (0~9)
  hasPlacedBet?: boolean; // 베팅 완료 여부 (Private Betting용)
  actionCompleted?: boolean; // 액션 완료 여부
  isDoubledDown?: boolean; // 더블다운 여부
  isSurrendered?: boolean; // 항복 여부
  isEliminated?: boolean; // 파산으로 제외 여부
  isAllIn?: boolean; // 올인 여부
}

// 딜러잭 게임 모드 열거형
export enum DealerJackGameMode {
  DEALER = "dealer", // 딜러와 대전 (기본 모드)
}

// 하위 호환성을 위한 alias
export const BlackjackGameMode = DealerJackGameMode;

// 딜러잭 게임 설정 인터페이스
export interface DealerJackGameSettings {
  gameMode: DealerJackGameMode;
  totalRounds: number;
  startingChips: number; // 50-200, 10 단위
  bettingTime: number; // 베팅 시간 (초)
  turnTime: number; // 턴 시간 (초)
  minBet: number; // 최소 배팅 칩수 (5, 10, 20)
}

// 라운드 결과 인터페이스
export interface RoundResult {
  playerId: number;
  handValue: number;
  isBlackjack: boolean;
  isBust: boolean;
  dealerValue: number;
  result: "win" | "lose" | "push"; // 승/패/무승부
  payout: number; // 배당금
  finalChips: number; // 결과 후 칩 수
}

// 소켓 프로토콜 데이터 타입
export interface BlackjackProtocolData {
  [BlackjackProtocol.GAME_START]: {
    players: BlackjackPlayer[];
    gameSettings: BlackjackGameSettings;
    hostId: number;
  };

  [BlackjackProtocol.ROUND_START]: {
    round: number;
    totalRounds: number;
    bettingTime: number;
    players: BlackjackPlayer[]; // 칩과 상태 초기화된 플레이어들
  };

  [BlackjackProtocol.BET_PLACED]: {
    playerId: number;
    amount: number;
    playerChips: number; // 베팅 후 남은 칩
  };

  [BlackjackProtocol.BETTING_TIME_END]: {
    players: BlackjackPlayer[]; // 베팅이 완료된 플레이어들
    allBets?: {
      [playerId: number]: {
        playerId: number;
        amount: number;
        playerChips: number;
        isAllIn?: boolean;    // 올인 여부
        isAutoBet?: boolean;  // 자동 베팅 여부
      }
    };
  };

  [BlackjackProtocol.CARDS_DEALT]: {
    playerCards: { [playerId: number]: Card[] }; // 각 플레이어의 초기 카드 (2장)
    dealerCards: Card[]; // 딜러의 카드 (1장은 뒤집어져 있음)
    dealerVisibleValue: number; // 딜러의 공개된 카드 값
  };

  [BlackjackProtocol.PLAYER_TURN]: {
    playerId: number;
    timeLimit: number;
    canHit: boolean;
    canStand: boolean;
    canDoubleDown?: boolean; // 첫 번째 액션으로만 가능
    canSurrender?: boolean; // 첫 번째 액션으로만 가능
  };

  [BlackjackProtocol.CARD_HIT]: {
    playerId: number;
    card: Card;
    newCards: Card[]; // 업데이트된 전체 카드
    handValue: number;
    isBust: boolean;
  };

  [BlackjackProtocol.PLAYER_STAND]: {
    playerId: number;
  };

  [BlackjackProtocol.PLAYER_ACTION]: {
    playerId: number;
    action: "hit" | "stand" | "double_down" | "surrender";
    newCard?: Card; // HIT, DOUBLE_DOWN 액션일 때만 포함
    handValue?: number;
    isBust?: boolean; // HIT, DOUBLE_DOWN 액션일 때만 포함
    canContinue?: boolean; // HIT 액션일 때만 포함
    isStanding?: boolean; // STAND, DOUBLE_DOWN 액션일 때만 포함
    confirmed?: boolean; // STAND, DOUBLE_DOWN, SURRENDER 액션일 때만 포함
    currentBet?: number; // DOUBLE_DOWN 액션일 때 업데이트된 베팅액
    remainingChips?: number; // DOUBLE_DOWN, SURRENDER 액션일 때 업데이트된 칩 수
    returnAmount?: number; // SURRENDER 액션일 때 반환되는 금액
  };

  [BlackjackProtocol.PLAYER_ACTION_COMPLETED]: {
    playerId: number;
    playerName: string;
    actionCompleted: true;
    remainingPlayers: number;
    // Updated player state for all clients to see
    playerState?: {
      handValue: number;
      cards: Card[];
      isBust: boolean;
      isStanding: boolean;
      isBlackjack: boolean;
    };
  };

  [BlackjackProtocol.DEALER_TURN]: {
    dealerCards: Card[]; // 딜러의 모든 카드 (뒤집힌 카드 포함)
    dealerValue: number;
    isComplete: boolean; // 딜러 턴 완료 여부
    // 모든 플레이어의 최종 액션 상태 (액션 턴 종료 후 동기화용)
    finalPlayerStates?: {
      playerId: number;
      cards: Card[];
      handValue: number;
      isBust: boolean;
      isStanding: boolean;
      isBlackjack: boolean;
      isActive: boolean;
    }[];
  };

  [BlackjackProtocol.ROUND_RESULTS]: {
    round: number;
    results: RoundResult[];
    dealerFinalCards: Card[];
    dealerFinalValue: number;
    nextRoundIn?: number; // 다음 라운드까지 시간 (초)
  };

  [BlackjackProtocol.GAME_END]: {
    finalScores?: { playerId: number; chips: number; profit: number }[];
    winner?: number; // 가장 많은 칩을 가진 플레이어
    rankings?: { playerId: number; rank: number; chips: number }[];
    finalRankings?: { playerId: number; rank: number; chips: number }[];
    totalRounds?: number;
    gameSettings?: any;
  };

  [BlackjackProtocol.PLAYER_ELIMINATED]: {
    eliminatedPlayers: {
      playerId: number;
      playerName: string;
      eliminationReason: "bankruptcy";
      finalChips: number;
    }[];
  };

  [BlackjackProtocol.SYNC_GAME_STATE]: {
    currentPhase: "betting" | "dealing" | "player_turn" | "dealer_turn" | "results" | "game_end";
    currentRound: number;
    totalRounds: number;
    timeLeft: number;
    players: BlackjackPlayer[];
    dealerCards?: Card[];
    dealerVisibleValue?: number;
    gameSettings: BlackjackGameSettings;
  };

  [BlackjackProtocol.ERROR]: {
    message: string;
    code: "ALL_IN_REQUIRED" | "MIN_BET_REQUIRED" | string;
    playerId?: number; // 특정 플레이어 에러인 경우
    minBet?: number; // MIN_BET_REQUIRED 에러용
  };
}

// 하위 호환성을 위한 alias들
export type BlackjackPlayer = DealerJackPlayer;
export type BlackjackGameSettings = DealerJackGameSettings;
